import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { money, postCheckout } from "@/lib/api";

const FREE_SHIP = 100;

export const CartDrawer = () => {
  const { cartOpen, setCartOpen, cart, updateQty, removeFromCart, subtotal, guestId } = useStore();
  const [loading, setLoading] = useState(false);
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  const checkout = async () => {
    setLoading(true);
    try {
      const items = cart.map((c) => ({ variant_id: c.variantId, quantity: c.quantity, title: c.title, price: c.price }));
      const { checkout_url } = await postCheckout({ items, guest_id: guestId });
      toast.success("Handing off to secure Shopify checkout…");
      window.location.href = checkout_url;
    } catch { toast.error("Checkout unavailable. Try again."); setLoading(false); }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" data-testid="cart-drawer" className="bg-[#F4F2ED] border-l border-black/10 w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 py-5 border-b border-black/10">
          <SheetTitle className="font-display uppercase tracking-widest text-sm text-left">Your Bag ({cart.reduce((s, c) => s + c.quantity, 0)})</SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping bag</SheetDescription>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 text-center">
            <ShoppingBag size={40} strokeWidth={1} className="text-black/30" />
            <p className="font-body text-black/50">Your bag is empty — but the drop isn't.</p>
            <Link to="/collections/new" onClick={() => setCartOpen(false)} className="btn-fill" data-testid="empty-cart-shop-btn">Shop New Arrivals</Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-black/10">
              {remaining > 0 ? (
                <p className="font-body text-xs text-black/60">You're <b>{money(remaining)}</b> away from free shipping</p>
              ) : (
                <p className="font-body text-xs text-[#0A0A0A]"><b>✓ Free shipping unlocked</b></p>
              )}
              <div className="h-1 bg-black/10 mt-2"><div className="h-full bg-[#0A0A0A] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 hide-scrollbar">
              {cart.map((c) => (
                <div key={c.variantId} data-testid={`cart-item-${c.variantId}`} className="flex gap-4">
                  <div className="w-20 h-24 bg-[#EAE8E2] shrink-0 overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-display text-xs uppercase tracking-tight leading-tight">{c.title}</h4>
                      <button onClick={() => removeFromCart(c.variantId)} data-testid={`cart-remove-${c.variantId}`} aria-label="Remove"><X size={15} /></button>
                    </div>
                    <p className="overline text-black/40 mt-1">Size {c.size}{c.color ? ` · ${c.color}` : ""}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-black/20">
                        <button onClick={() => updateQty(c.variantId, c.quantity - 1)} data-testid={`cart-dec-${c.variantId}`} className="w-8 h-8 flex items-center justify-center"><Minus size={13} /></button>
                        <span className="w-8 text-center font-mono text-sm">{c.quantity}</span>
                        <button onClick={() => updateQty(c.variantId, c.quantity + 1)} data-testid={`cart-inc-${c.variantId}`} className="w-8 h-8 flex items-center justify-center"><Plus size={13} /></button>
                      </div>
                      <span className="font-mono text-sm">{money(c.price * c.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-5 border-t border-black/10 space-y-4">
              <div className="flex justify-between font-display uppercase tracking-widest text-sm">
                <span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="btn-fill w-full disabled:opacity-60">
                <Lock size={14} /> {loading ? "Redirecting…" : "Secure Checkout"}
              </button>
              <p className="font-mono text-[10px] text-center text-black/40">Payment secured by Shopify · Taxes at checkout</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
